class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.mobile-menu__panel');
    this.backdrop = this.querySelector('.mobile-menu__backdrop');
    // Any control on the page can open this drawer (the global header
    // hamburger and the home hero's own hamburger both target it).
    this.toggles = Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
    if (this.toggles.length === 0 || !this.panel) return;

    this._focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

    this._onToggleClick = this._onToggleClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);

    this.toggles.forEach((toggle) => toggle.addEventListener('click', this._onToggleClick));
    this.backdrop?.addEventListener('click', this._onBackdropClick);
  }

  disconnectedCallback() {
    this.toggles?.forEach((toggle) => toggle.removeEventListener('click', this._onToggleClick));
    this.backdrop?.removeEventListener('click', this._onBackdropClick);
    document.removeEventListener('keydown', this._onKeydown);
    document.body.style.overflow = '';
  }

  get isOpen() {
    return this.getAttribute('aria-hidden') === 'false';
  }

  _syncToggles(open) {
    this.toggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        toggle.setAttribute('data-open', 'true');
        toggle.setAttribute('aria-label', toggle.dataset.closeLabel || 'Close menu');
      } else {
        toggle.removeAttribute('data-open');
        toggle.setAttribute('aria-label', toggle.dataset.openLabel || 'Open menu');
      }
    });
  }

  open() {
    if (this.isOpen) return;

    // Remember the control that opened the drawer so focus returns to it on
    // close — it's guaranteed visible, unlike a toggle that may be closed via
    // a different (and possibly now-hidden) control.
    this._openerToggle = this._activeToggle || this.toggles[0];

    this.setAttribute('aria-hidden', 'false');
    this._syncToggles(true);

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKeydown);

    this.dispatchEvent(new CustomEvent('mobile-menu:open', { bubbles: true }));

    requestAnimationFrame(() => {
      const first = this.panel.querySelector(this._focusableSelector);
      if (first) first.focus();
    });
  }

  close() {
    if (!this.isOpen) return;

    this.setAttribute('aria-hidden', 'true');
    this._syncToggles(false);

    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._onKeydown);

    this.dispatchEvent(new CustomEvent('mobile-menu:close', { bubbles: true }));
    // Return focus to the control that opened the drawer (always visible).
    (this._openerToggle || this.toggles[0]).focus();
  }

  _onToggleClick(e) {
    e.preventDefault();
    this._activeToggle = e.currentTarget;
    this.isOpen ? this.close() : this.open();
  }

  _onBackdropClick() {
    this.close();
  }

  _onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }

    if (e.key === 'Tab') {
      const focusables = Array.from(this.panel.querySelectorAll(this._focusableSelector));
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
}

if (!customElements.get('mobile-menu')) {
  customElements.define('mobile-menu', MobileMenu);
}
